import { useTheme } from '@emotion/react'
import { AppState, ConfigurationContext } from '@news-mono/web-common'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useLocation } from 'react-router-dom'
import { Skeleton } from '../../../../src/content/Skeleton/Skeleton'
import { useAuthActions } from '../../../user-registration/authentication/AuthenticationProvider'
import { themedValue } from '../../../__styling'
import {
    StyledBody,
    StyledButton,
    StyledLogin,
    StyledUnsubscribe,
} from '../../components/Unsubscribe.styled'
import { EPC_STORAGE_KEY, ProfileStorage } from '../../EmailPreference'
import {
    StyledEmailPreference,
    StyledFooter,
    StyledGreeting,
    StyledHeading,
    StyledLink,
    StyledWebLink,
} from '../../EmailPreference.styled'
import {
    authClickHandler,
    useGetEmailToken,
} from '../../hooks/useEmailPreferenceData'
import { getStorage, useResolveEmail } from '../../hooks/useUnsubscribe'
import { StyledBoldP } from './UnsubscribeAll.styled'

export const UnsubscribeAll = () => {
    const theme = useTheme()
    const conatctLink = themedValue(theme, {
        thewest: '/contact',
        perthnow: '/contact-us',
        sevennews: '/contact-us',
    })
    const { emailServiceApi } = useContext(ConfigurationContext)
    const { onLoginClick, onRegisterClick } = useAuthActions()
    const history = useHistory()
    // Get sessionStorage to decide which email to display
    const storageData = getStorage(EPC_STORAGE_KEY) as ProfileStorage | null
    const tokenRef = useRef<{
        urlToken?: string
        profileToken?: string
    }>()
    const [useProfileEmail, setUseProfileEmail] = useState<boolean>(false)

    // Token from URL query param
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const emailTokenFromQueryParam = queryParams.get('token')

    // Token from redux store
    const {
        emailToken: emailTokenFromReduxStore,
        userEmail: emailFromReduxStore,
        isLoggedIn,
    } = useSelector((state: AppState) => state.authentication)

    const {
        isLoading: isEmailLoading,
        resolveEmailError,
        data: emailsData,
    } = useResolveEmail(
        false,
        emailServiceApi,
        emailTokenFromQueryParam ?? undefined,
        emailFromReduxStore,
    )
    const emailFromEmailService = emailsData.tokenEmail

    const emailToken = isLoggedIn
        ? emailTokenFromReduxStore
        : emailTokenFromQueryParam
    const email = isLoggedIn ? emailFromReduxStore : emailFromEmailService

    const {
        data: tokenData,
        isLoading,
        isError,
    } = useGetEmailToken({
        enabled: isLoggedIn,
    })
    if (tokenData?.token) {
        tokenRef.current = {
            ...tokenRef.current,
            profileToken: tokenData?.token,
        }
    }

    const [success, setSuccess] = useState(false)
    const [error, setError] = useState<{ message: string }>()
    useEffect(() => {
        if (emailTokenFromQueryParam) {
            tokenRef.current = {
                ...tokenRef.current,
                urlToken: emailTokenFromQueryParam,
            }
        }
        const useProfile =
            !emailTokenFromQueryParam ||
            (emailTokenFromQueryParam &&
                storageData &&
                storageData.useProfileEmail)
        setUseProfileEmail(!!useProfile)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleUnsubscribeAll = async () => {
        // If logged in and (isLoading or isError or no token data) we do not want to send request
        if (
            isLoggedIn &&
            (!tokenData || isLoading || isError || isEmailLoading)
        )
            return

        if (!emailServiceApi) {
            setError({
                message: 'Email service api endpoint not defined',
            })
            return
        }

        if (!emailToken || !email) {
            setError({
                message: 'Required payload missing',
            })
            return
        }

        const response = await fetch(
            `${emailServiceApi}/email-preference-center/unsubscribe/all`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: useProfileEmail
                        ? tokenRef.current?.profileToken || ''
                        : tokenRef.current?.urlToken || '',
                },
                body: JSON.stringify({
                    payload: {
                        email: useProfileEmail
                            ? emailsData.profileEmail
                            : emailsData.tokenEmail,
                    },
                }),
            },
            // `fetch` doesn't reject status >= 400 && status < 600
            // So need to catch it here as well
        ).catch((error) => {
            setError({ message: error })
            throw new Error(error)
        })

        if (response.status === 200) {
            setSuccess(true)
        } else {
            const message = 'Error submitting form'
            setError({ message })
            throw new Error(message)
        }
    }

    const currentToken = tokenRef.current?.urlToken || null
    const goBackLink = currentToken
        ? `/manage-email-preferences?token=${currentToken}`
        : `/manage-email-preferences`

    if (success)
        return (
            <UnsubscribeAllSuccess
                email={
                    useProfileEmail
                        ? emailsData.profileEmail
                        : emailsData.tokenEmail
                }
                goBackLink={goBackLink}
                contactLink={conatctLink}
            />
        )

    if (error) {
        throw new Error(error.message)
    }

    function onSwitchEmailClick(e: React.MouseEvent<HTMLElement>) {
        e.preventDefault()
        setUseProfileEmail((useProfileEmail) => !useProfileEmail)
        sessionStorage.setItem(
            EPC_STORAGE_KEY,
            JSON.stringify({
                useProfileEmail: !useProfileEmail,
            }),
        )
    }

    return (
        <>
            <StyledEmailPreference>
                <StyledUnsubscribe>
                    <StyledHeading>Unsubscribe confirmation</StyledHeading>
                    {(isLoggedIn || emailTokenFromQueryParam) && (
                        <StyledGreeting>
                            Preferences for:{' '}
                            {(useProfileEmail
                                ? emailsData.profileEmail
                                : emailsData.tokenEmail) || (
                                <Skeleton height={25} width={170} />
                            )}
                        </StyledGreeting>
                    )}
                    {!isLoggedIn && (
                        <StyledLogin>
                            {emailTokenFromQueryParam
                                ? `Email address incorrect? `
                                : `You haven't logged in. Please `}
                            <StyledLink
                                onClick={(e) =>
                                    authClickHandler(
                                        e,
                                        EPC_STORAGE_KEY,
                                        onLoginClick,
                                    )
                                }
                            >
                                Login
                            </StyledLink>
                            &nbsp;or&nbsp;
                            <StyledLink
                                onClick={(e) =>
                                    authClickHandler(
                                        e,
                                        EPC_STORAGE_KEY,
                                        onRegisterClick,
                                    )
                                }
                            >
                                Register
                            </StyledLink>
                            .
                        </StyledLogin>
                    )}
                    {isLoggedIn &&
                        currentToken &&
                        emailsData.profileEmail !== emailsData.tokenEmail && (
                            <StyledLogin>
                                Switch to unsubscribe for{' '}
                                {
                                    <strong>
                                        {!useProfileEmail
                                            ? emailsData.profileEmail
                                            : emailsData.tokenEmail}
                                    </strong>
                                }
                                ?&nbsp;
                                <StyledLink
                                    onClick={(e) => onSwitchEmailClick(e)}
                                >
                                    Click here
                                </StyledLink>
                            </StyledLogin>
                        )}

                    <StyledBody>
                        <p>You are unsubscribing from:</p>
                        <StyledBoldP>
                            All marketing and promotional material
                        </StyledBoldP>

                        <StyledButton
                            isPrimary={true}
                            onClick={handleUnsubscribeAll}
                            isLoading={!emailToken || isLoading || !email}
                        >
                            Unsubscribe
                        </StyledButton>
                        <StyledButton
                            isPrimary={false}
                            onClick={() => {
                                history.push(goBackLink)
                            }}
                        >
                            Cancel
                        </StyledButton>

                        <StyledWebLink to={goBackLink}>
                            Manage email preferences
                        </StyledWebLink>
                    </StyledBody>
                    <StyledFooter>
                        <h2>Looking for something else?</h2>
                        <StyledWebLink to={conatctLink}>
                            Contact us
                        </StyledWebLink>
                    </StyledFooter>
                </StyledUnsubscribe>
            </StyledEmailPreference>
        </>
    )
}

const UnsubscribeAllSuccess = ({
    email,
    goBackLink,
    contactLink,
}: {
    email: string | undefined
    goBackLink: string
    contactLink: string
}) => {
    const history = useHistory()
    function onClickCancel(e: React.MouseEvent<HTMLElement>) {
        e.preventDefault()
        history.push(goBackLink)
    }
    return (
        <>
            <StyledEmailPreference>
                <StyledUnsubscribe>
                    <StyledHeading>We hope to see you again soon</StyledHeading>
                    <StyledGreeting>Preferences for: {email}</StyledGreeting>
                    <StyledBody>
                        <p>You have successfully unsubscribed from:</p>
                        <StyledBoldP>
                            All marketing and promotional material
                        </StyledBoldP>
                        <br />
                        <StyledButton
                            isPrimary={false}
                            onClick={(e) => onClickCancel(e)}
                        >
                            Manage email preferences
                        </StyledButton>
                    </StyledBody>
                    <StyledFooter>
                        <h2>Looking for something else?</h2>
                        <StyledWebLink to={contactLink}>
                            Contact us
                        </StyledWebLink>
                    </StyledFooter>
                </StyledUnsubscribe>
            </StyledEmailPreference>
        </>
    )
}
